import React, { type HTMLProps, type ReactNode } from 'react';
import { IconCardHotel } from '@lmn/lastminute-react-icons/lastminute-icons-linear.js';
import styled, { css, useTheme } from 'styled-components';
import { useTranslation } from '@/app/components/translations/Translation';
import { Card } from '@lmn/design-system';

const StyledToastIcon = styled(IconCardHotel)`
  color: ${({ theme }) => theme.commons.colors.success};
  min-height: 52px;
  min-width: 52px;
`;

export function PayAtHotelBanner(props: React.HTMLAttributes<HTMLElement>) {
  const { ...rest } = props;
  const { t, translateWithElementPlaceholder } = useTranslation();

  return (
    <div
      {...rest}
      css={css`
        background-color: ${({ theme }) => theme.commons.colors.gray0};
        border-radius: 8px;
      `}
    >
      <Message type={'success'} key={'pay-at-hotel-message'}>
        <div
          css={css`
            display: flex;
            padding: 16px;
            gap: 16px;
          `}
        >
          <StyledToastIcon />
          <div
            css={css`
              display: flex;
              flex-direction: column;
            `}
          >
            <span
              css={css`
                ${({ theme }) => theme.kallisto.mixins.font('Title4')};
              `}
            >
              {t('vacation.select.details.paymentbox.title')}
            </span>
            <span>
              {translateWithElementPlaceholder(
                'vacation.select.details.paymentbox.payathotel',
                'paymentType',
                <strong key="pay-at-hotel">&quot;{t('vacation.select.details.payathotel')}&quot;</strong>
              )}
            </span>
          </div>
        </div>
      </Message>
    </div>
  );
}

type MessageProps = {
  children: ReactNode;
  /** delay to hide the toast in milliseconds. `-1` will disable the timeout. */
  timeout?: number;
  type?: 'success' | 'warning' | 'danger';
} & HTMLProps<HTMLDivElement>;

function Message(props: MessageProps) {
  const { children, type = 'success', ...rest } = props;
  const theme = useTheme();
  const color = theme.commons.colors[type];

  return (
    <Card
      isClickable={false}
      isOutlined={false}
      radius="2px"
      elevation="medium"
      padding={0}
      {...rest}
      css={css`
        background:
          linear-gradient(${color}40, ${color}40),
          linear-gradient(${theme.commons.colors.gray0}, ${theme.commons.colors.gray0});
        border: 1px solid ${color}20;
        display: flex;
        font-size: ${(p) => p.theme.commons.font.sizes.base};
        pointer-events: auto;
      `}
    >
      {children}
    </Card>
  );
}
